"use client";

import dynamic from 'next/dynamic';
import { useEffect, useState } from "react";
import { CanvasRevealEffectProps } from "./CanvasReveal";

// Dynamically import the CanvasRevealEffect component with SSR disabled
// This ensures it only loads on the client side
const CanvasRevealEffectDynamic = dynamic(
  () => import('./CanvasReveal').then(mod => ({ default: mod.CanvasRevealEffect })),
  { 
    ssr: false, // Disable server-side rendering
    loading: () => null // Don't show anything while loading
  }
);

export default function DynamicCanvasReveal(props: CanvasRevealEffectProps) {
  const [isPageRendered, setIsPageRendered] = useState(false);

  useEffect(() => {
    // Use requestIdleCallback if available, otherwise use setTimeout
    if (typeof window !== 'undefined') {
      if ('requestIdleCallback' in window) {
        // Wait until the browser is idle to load the animation
        const idleCallbackId = (window as any).requestIdleCallback(() => {
          setIsPageRendered(true);
        }, { timeout: 2000 }); // 2 second timeout as fallback
        
        return () => {
          if ('cancelIdleCallback' in window) {
            (window as any).cancelIdleCallback(idleCallbackId);
          }
        };
      } else {
        // Fallback for browsers that don't support requestIdleCallback
        const timeoutId = setTimeout(() => {
          setIsPageRendered(true);
        }, 500); // Wait 500ms after initial render
        
        return () => clearTimeout(timeoutId);
      }
    }
  }, []);

  // Don't render anything until the page has rendered and browser is idle
  if (!isPageRendered) {
    return null;
  }

  // Once the page has rendered and browser is idle, render the dynamically imported CanvasRevealEffect
  return <CanvasRevealEffectDynamic {...props} />;
}
